import React, { useState, useEffect, useMemo } from 'react';
import { Steps, Form, Button, message } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAssetPurchaseTypes, getCurrencies, PurchaseTypeOption, createDamAsset, createBatchDamAssets, createMarketplaceListing, createWearableItem, createSkillItem, createWorkflowItem, getDigitalAssetTypes, parseWearableFile } from '../api/marketplace';
import { imageFileUpload } from './SignupHooks';
import { getStoredUser } from '../utils/authUtils';
import CommonHeader from './CommonHeader';
import styles from './AddItemForm.module.scss';
import {
  BasicDetails, UploadFiles, LicenseTerms, PricingStep, ReviewStep,
  WearableUpload, WearableDetails, BulkWearableDetails, BulkPricingStep, BulkReviewStep,
  SkillUpload, SkillDetails,
  WorkflowUpload, WorkflowDetails,
} from './AddItemSteps';
import type { AddItemFormData, BulkWearableItem, PricingOption } from './AddItemSteps';

const { Step } = Steps;

const AddItemForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const itemTypeParam = searchParams.get('type') || '';
  
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [damId, setDamId] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [isWearable, setIsWearable] = useState(itemTypeParam === 'wearables');
  const [isSkill, setIsSkill] = useState(itemTypeParam === 'skill');
  const [isWorkflow, setIsWorkflow] = useState(itemTypeParam === 'workflow');
  const [isBulkUpload, setIsBulkUpload] = useState(false);
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [bulkItems, setBulkItems] = useState<BulkWearableItem[]>([]);
  const [bulkResponseRaw, setBulkResponseRaw] = useState<any[]>([]);
  const [digitalAssetTypeId, setDigitalAssetTypeId] = useState<string>('');
  const [availablePurchaseTypes, setAvailablePurchaseTypes] = useState<PurchaseTypeOption[]>([]);
  const [assetTypeMongoId, setAssetTypeMongoId] = useState<string>('');
  const [usdCurrencyId, setUsdCurrencyId] = useState<string>('');
  const [formData, setFormData] = useState<AddItemFormData>({
    itemType: itemTypeParam,
    itemName: '',
    industry: '',
    language: '',
    type: '',
    tags: '',
    description: '',
    wearableType: '',
    material: '',
    style: '',
    fabric: '',
    itemUpload: [],
    thumbnail: [],
    videoDemo: [],
    terms_of_use: '',
    privacy_policy: '',
    pricingModel: [],
    pricingDetails: {},
    supportedInterfaces: [],
    agentName: 'Alex Johnson',
    agentType: 'Virtual Assistant',
    agentDescription: 'Experienced virtual assistant with a proven track record of providing exceptional administrative support to business of all sizes.',
    agentSkills: 'Calendar Management, Email Handling, Customer Service',
    agentAvailability: 'Available Monday to Friday, 9 AM to 5 PM'
  });

  // Redirect to dashboard if no item type is selected
  useEffect(() => {
    if (!itemTypeParam) {
      navigate('/dashboard');
    }
  }, [itemTypeParam, navigate]);

  // Fetch digital asset types on component mount or when item type changes
  useEffect(() => {
    const fetchAssetTypes = async () => {
      try {
        // Fetch digital asset types filtered by the selected item type
        const result = await getDigitalAssetTypes(itemTypeParam);
        if (result.statusCode === 200 && result.data) {          
          setDigitalAssetTypeId(result.data);
          console.log('Fetched digital asset type ID:', result.data);
        }
      } catch (error) {
        console.error('Error fetching digital asset types:', error);
        // If API call fails, leave the map empty - API call must succeed for form to work properly
        setDigitalAssetTypeId("");

      }
    };
    
    if (itemTypeParam) {
      fetchAssetTypes();
    }
  }, [itemTypeParam]);

  // Fetch currencies and find USD _id
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const result = await getCurrencies();
        if (result.statusCode === 200 && result.data) {
          const usd = result.data.find(c => c.code === 'USD');
          if (usd) {
            setUsdCurrencyId(usd._id);
          }
        }
      } catch (error) {
        console.error('Error fetching currencies:', error);
      }
    };
    fetchCurrencies();
  }, []);

  // Fetch purchase types for the current asset type
  useEffect(() => {
    const fetchPurchaseTypes = async () => {
      if (!digitalAssetTypeId) return;
      try {
        const result = await getAssetPurchaseTypes();
        if (result.statusCode === 200 && result.data) {
          const matched = result.data.find(item => item.assetType === digitalAssetTypeId);
          if (matched) {
            setAvailablePurchaseTypes(matched.purchaseTypes);
            setAssetTypeMongoId(matched._id);
          }
        }
      } catch (error) {
        console.error('Error fetching asset purchase types:', error);
      }
    };
    fetchPurchaseTypes();
  }, [digitalAssetTypeId]);

  const stepsNormal = [
    { title: 'Basic Details', content: 'Step 1 content' },
    { title: 'Upload Files', content: 'Step 2 content' },
    { title: 'License Terms', content: 'Step 3 content' },
    { title: 'Pricing and Interfaces', content: 'Step 4 content' },
    { title: 'Review and Submit', content: 'Step 5 content' }
  ];

  const stepsWearable = [
    { title: 'Upload Files', content: 'Step 1 content' },
    { title: 'Basic Details', content: 'Step 2 content' },
    { title: 'Pricing and Interfaces', content: 'Step 3 content' },
    { title: 'Review and Submit', content: 'Step 4 content' }
  ];

  const stepsSkill = [
    { title: 'Upload Files', content: 'Step 1 content' },
    { title: 'Basic Details', content: 'Step 2 content' },
    { title: 'Pricing and Interfaces', content: 'Step 3 content' },
    { title: 'Review and Submit', content: 'Step 4 content' }
  ];

  const stepsWorkflow = [
    { title: 'Upload Files', content: 'Step 1 content' },
    { title: 'Basic Details', content: 'Step 2 content' },
    { title: 'Pricing and Interfaces', content: 'Step 3 content' },
    { title: 'Review and Submit', content: 'Step 4 content' }
  ];

  const steps = isWearable ? stepsWearable : isSkill ? stepsSkill : isWorkflow ? stepsWorkflow : stepsNormal;

  const purchaseTypeLabelMap: { [key: string]: { label: string; desc: string } } = {
    renting: { label: 'Monthly Subscription', desc: 'Charge a recurring fee for access to your agent' },
    purchasing: { label: 'One-time Purchase', desc: 'Charge a single fee for lifetime access to your agent' },
    payPerUse: { label: 'Pay-Per Usage', desc: 'Charge based on the number of times your agent is used' },
    free: { label: 'Freemium', desc: 'Offer a free version of your agent with limited features' },
    freemium: { label: 'Freemium', desc: 'Offer a free version of your agent with limited features' },
  };

  const pricingOptions: PricingOption[] = useMemo(() => availablePurchaseTypes.map(pt => ({
    value: pt.purchaseType,
    id: pt._id,
    label: purchaseTypeLabelMap[pt.purchaseType]?.label || pt.purchaseType,
    desc: purchaseTypeLabelMap[pt.purchaseType]?.desc || '',
    allowMultiple: pt.allowMultiple,
  })), [availablePurchaseTypes]);

  const handleNext = async () => {
    setIsSubmitted(true);
    
    try {
      // Validate current step's fields
      await form.validateFields();
      
      if (isWearable) {
        // Wearables flow
        if (currentStep === 0) {
          // If bulk upload is active, upload the Excel file
          if (isBulkUpload) {
            if (!excelFile) {
              messageApi.open({
                type: 'error',
                content: 'Please upload an Excel file',
              });
              return;
            }
            try {
              setIsImageUploading(true);
              const parseResult = await parseWearableFile(excelFile);

              if (parseResult.statusCode === 200 || parseResult.statusCode === 201) {
                // Flatten response into individual items for the form
                const rawData = parseResult.data?.data || [];
                setBulkResponseRaw(rawData);
                const items: BulkWearableItem[] = [];
                rawData.forEach((group: any) => {
                  (group.itemList || []).forEach((item: any) => {
                    const getAttr = (traitType: string) =>
                      item.itemMetadata?.attributes?.find(
                        (a: any) => a.trait_type?.toLowerCase() === traitType.toLowerCase()
                      )?.value || '';
                    items.push({
                      wearableId: group.wearableId || '',
                      itemId: item.itemId || '',
                      itemName: item.itemMetadata?.name || '',
                      description: item.itemMetadata?.description || '',
                      image: item.itemMetadata?.image || '',
                      wearableType: getAttr('Category') || getAttr('wearableType') || '',
                      material: getAttr('Material') || getAttr('material') || '',
                      style: getAttr('Style') || getAttr('style') || '',
                      fabric: getAttr('Fabric') || getAttr('fabric') || '',
                      tags: getAttr('tags') || '',
                      pricingModel: [],
                      pricingDetails: {},
                    });
                  });
                });
                setBulkItems(items);
                // Set parsed values into form fields
                form.setFieldsValue({
                  bulkItems: items.map(item => ({
                    wearableType: item.wearableType || undefined,
                    material: item.material || undefined,
                    style: item.style || undefined,
                    fabric: item.fabric || undefined,
                    itemName: item.itemName || undefined,
                    tags: item.tags || undefined,
                    description: item.description || undefined,
                  }))
                });
                messageApi.open({
                  type: 'success',
                  content: 'Excel file parsed successfully',
                });
              } else {
                const errorMsg = parseResult.message || 'Failed to parse Excel file';
                messageApi.open({
                  type: 'error',
                  content: errorMsg,
                });
                setIsImageUploading(false);
                return;
              }
            } catch (error: any) {
              console.error('Excel parse error:', error);
              const errorMessage = error.response?.data?.message || 'Excel file upload failed';
              messageApi.open({
                type: 'error',
                content: errorMessage,
              });
              setIsImageUploading(false);
              return;
            }
            setIsImageUploading(false);
            setCurrentStep(currentStep + 1);
          } else {
          // Upload image if available
          if (formData.thumbnail && formData.thumbnail.length > 0) {
            try {
              setIsImageUploading(true);
              const uploadResponse = await imageFileUpload(formData.thumbnail[0]);
              
              if (uploadResponse.publicUrl) {
                setFormData(prev => ({
                  ...prev,
                  iconUrl: uploadResponse.publicUrl
                }));
                messageApi.open({
                  type: 'success',
                  content: 'Image uploaded successfully',
                });
              } else {
                messageApi.open({
                  type: 'error',
                  content: 'Failed to upload image',
                });
                setIsImageUploading(false);
                return;
              }
            } catch (error: any) {
              console.error('Image upload error:', error);
              const errorMessage = error.response?.data?.message || 'Image upload failed';
              messageApi.open({
                type: 'error',
                content: errorMessage,
              });
              setIsImageUploading(false);
              return;
            }
          }
          setIsImageUploading(false);
          setCurrentStep(currentStep + 1);
          } // end else (non-bulk upload)
        } else if (currentStep === 1) {
          if (isBulkUpload) {
            // Validate bulk items from form fields
            const formValues = form.getFieldsValue();
            const bulkFormItems = formValues.bulkItems || [];
            const hasInvalid = bulkFormItems.some((item: any) =>
              !item?.wearableType || !item?.material || !item?.style || !item?.fabric
            );
            if (bulkFormItems.length === 0 || hasInvalid) {
              messageApi.open({
                type: 'error',
                content: 'Please fill in all wearable details for each item',
              });
              return;
            }
            // Sync bulk form values back to bulkItems state
            setBulkItems(prev =>
              prev.map((item, i) => ({
                ...item,
                ...bulkFormItems[i],
              }))
            );
          } else {
            console.log(formData.wearableType,formData.material,formData.style,formData.fabric,"qwerty")
            // Wearable details - validate required fields
            if (!formData.wearableType || !formData.material || !formData.style || !formData.fabric) {
              messageApi.open({
                type: 'error',
                content: 'Please fill in all wearable details',
              });
              return;
            }
          }
          setCurrentStep(currentStep + 1);
        } else if (currentStep === 2) {
          if (isBulkUpload) {
            // Bulk: validate per-item pricing models and prices
            const hasMissingModel = bulkItems.some((item) => !item.pricingModel || item.pricingModel.length === 0);
            if (hasMissingModel) {
              messageApi.open({
                type: 'error',
                content: 'Please select at least one pricing model for all items',
              });
              return;
            }
            const hasNoPrice = bulkItems.some((item) => {
              const nonFreeModels = item.pricingModel.filter(m => m !== 'free' && m !== 'freemium');
              return nonFreeModels.some(m => {
                const price = item.pricingDetails[m]?.price;
                return price === undefined || price === null || price <= 0;
              });
            });
            if (hasNoPrice) {
              messageApi.open({
                type: 'error',
                content: 'Please enter a valid price for all non-free pricing options',
              });
              return;
            }
          } else {
            // Single: validate shared pricing model
            if (!formData.pricingModel || formData.pricingModel.length === 0) {
              messageApi.open({
                type: 'error',
                content: 'Please select a pricing model',
              });
              return;
            }
            const hasMissingPrice = formData.pricingModel
              .filter(m => m !== 'free' && m !== 'freemium')
              .some(m => !formData.pricingDetails[m]?.price || formData.pricingDetails[m]?.price <= 0);
            if (hasMissingPrice) {
              messageApi.open({
                type: 'error',
                content: 'Please enter a valid price for all selected pricing options',
              });
              return;
            }
          }
          setCurrentStep(currentStep + 1);
        } else {
          // For other steps, just move forward
          if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
          }
        }
      } else if (isWorkflow) {
        // Workflow flow (mirrors skill: Upload → Details → Pricing → Review)
        if (currentStep === 0) {
          if (!formData.thumbnail || formData.thumbnail.length === 0) {
            messageApi.open({ type: 'error', content: 'Please upload a thumbnail image' });
            return;
          }
          try {
            setIsImageUploading(true);
            const uploadResponse = await imageFileUpload(formData.thumbnail[0]);
            if (uploadResponse.publicUrl) {
              setFormData(prev => ({ ...prev, iconUrl: uploadResponse.publicUrl }));
              messageApi.open({ type: 'success', content: 'Image uploaded successfully' });
            } else {
              messageApi.open({ type: 'error', content: 'Failed to upload image' });
              setIsImageUploading(false);
              return;
            }
          } catch (error: any) {
            console.error('Image upload error:', error);
            const errorMessage = error.response?.data?.message || 'Image upload failed';
            messageApi.open({ type: 'error', content: errorMessage });
            setIsImageUploading(false);
            return;
          }
          setIsImageUploading(false);
          setCurrentStep(currentStep + 1);
        } else if (currentStep === 1) {
          setCurrentStep(currentStep + 1);
        } else if (currentStep === 2) {
          if (!formData.pricingModel || formData.pricingModel.length === 0) {
            messageApi.open({ type: 'error', content: 'Please select a pricing model' });
            return;
          }
          const hasMissingPrice = formData.pricingModel
            .filter(m => m !== 'free' && m !== 'freemium')
            .some(m => !formData.pricingDetails[m]?.price || formData.pricingDetails[m]?.price <= 0);
          if (hasMissingPrice) {
            messageApi.open({ type: 'error', content: 'Please enter a valid price for all selected pricing options' });
            return;
          }
          setCurrentStep(currentStep + 1);
        } else if (currentStep < steps.length - 1) {
          setCurrentStep(currentStep + 1);
        }
      } else if (isSkill) {
        // Skill flow (mirrors single wearable: Upload → Details → Pricing → Review)
        if (currentStep === 0) {
          if (!formData.thumbnail || formData.thumbnail.length === 0) {
            messageApi.open({ type: 'error', content: 'Please upload a thumbnail image' });
            return;
          }
          try {
            setIsImageUploading(true);
            const uploadResponse = await imageFileUpload(formData.thumbnail[0]);
            if (uploadResponse.publicUrl) {
              setFormData(prev => ({ ...prev, iconUrl: uploadResponse.publicUrl }));
              messageApi.open({ type: 'success', content: 'Image uploaded successfully' });
            } else {
              messageApi.open({ type: 'error', content: 'Failed to upload image' });
              setIsImageUploading(false);
              return;
            }
          } catch (error: any) {
            console.error('Image upload error:', error);
            const errorMessage = error.response?.data?.message || 'Image upload failed';
            messageApi.open({ type: 'error', content: errorMessage });
            setIsImageUploading(false);
            return;
          }
          setIsImageUploading(false);
          setCurrentStep(currentStep + 1);
        } else if (currentStep === 1) {
          // Skill basic-details validation handled by Form.Item rules via validateFields()
          setCurrentStep(currentStep + 1);
        } else if (currentStep === 2) {
          if (!formData.pricingModel || formData.pricingModel.length === 0) {
            messageApi.open({ type: 'error', content: 'Please select a pricing model' });
            return;
          }
          const hasMissingPrice = formData.pricingModel
            .filter(m => m !== 'free' && m !== 'freemium')
            .some(m => !formData.pricingDetails[m]?.price || formData.pricingDetails[m]?.price <= 0);
          if (hasMissingPrice) {
            messageApi.open({ type: 'error', content: 'Please enter a valid price for all selected pricing options' });
            return;
          }
          setCurrentStep(currentStep + 1);
        } else if (currentStep < steps.length - 1) {
          setCurrentStep(currentStep + 1);
        }
      } else {
        // Normal flow
        if (currentStep === 0) {
          // Just move to next step, no API call here
          setCurrentStep(currentStep + 1);
        } else if (currentStep === 1) {
          // Upload image if available
          if (formData.thumbnail && formData.thumbnail.length > 0) {
            try {
              setIsImageUploading(true);
              const uploadResponse = await imageFileUpload(formData.thumbnail[0]);
              
              if (uploadResponse.publicUrl) {
                setFormData(prev => ({
                  ...prev,
                  iconUrl: uploadResponse.publicUrl
                }));
                messageApi.open({
                  type: 'success',
                  content: 'Image uploaded successfully',
                });
              } else {
                messageApi.open({
                  type: 'error',
                  content: 'Failed to upload image',
                });
                setIsImageUploading(false);
                return;
              }
            } catch (error: any) {
              console.error('Image upload error:', error);
              const errorMessage = error.response?.data?.message || 'Image upload failed';
              messageApi.open({
                type: 'error',
                content: errorMessage,
              });
              setIsImageUploading(false);
              return;
            }
          }
          setIsImageUploading(false);
          setCurrentStep(currentStep + 1);
        } else if (currentStep === 2) {
          // Validate license terms
          if (!formData.terms_of_use || !formData.privacy_policy) {
            messageApi.open({
              type: 'error',
              content: 'Please fill in all license information',
            });
            return;
          }
          setCurrentStep(currentStep + 1);
        } else if (currentStep === 3) {
          // Validate pricing model
          if (formData.pricingModel.length === 0) {
            messageApi.open({
              type: 'error',
              content: 'Please select a pricing model',
            });
            return;
          }
          const hasMissingPrice = formData.pricingModel
            .filter(m => m !== 'free' && m !== 'freemium')
            .some(m => !formData.pricingDetails[m]?.price || formData.pricingDetails[m]?.price <= 0);
          if (hasMissingPrice) {
            messageApi.open({
              type: 'error',
              content: 'Please enter a valid price for all selected pricing options',
            });
            return;
          }
          setCurrentStep(currentStep + 1);
        } else {
          // For other steps (like step 5 - review), just move forward
          if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
          }
        }
      }
    } catch (error) {
      console.error('Form validation error:', error);
      messageApi.open({
        type: 'error',
        content: 'Please check your form entries and try again.',
      });
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  const handleSubmit = async () => {
    try {
      setIsFormSubmitting(true);
      
      // Handle wearable item submission
      if (isWearable) {
        const authUser = getStoredUser();
        const wearableCollectionId = process.env.REACT_APP_WEARABLE_COLLECTION_ID || '';

        if (isBulkUpload && bulkItems.length > 0) {
          // ---- BULK UPLOAD FLOW ----
          // Get latest values from the form
          const formValues = form.getFieldsValue();
          const bulkFormItems = formValues.bulkItems || [];

          // Step 1: Batch DAM API â€” create all assets at once
          const batchDamPayload = bulkItems.map((item, i) => {
            const formItem = bulkFormItems[i] || {};
            return {
              name: formItem.itemName || item.itemName,
              organizationId: authUser?.org_id || '',
              displayName: formItem.itemName || item.itemName,
              iconUrl: item.image || '',
              active: true,
              description: formItem.description || item.description || '',
              digitalAssetTypeId: digitalAssetTypeId,
              assetOrigin: 'internal',
              state: 'DA_ready_to_execute',
              // collectionId: wearableCollectionId,
              attributes: {
                itemType: formData.itemType,
                wearableType: formItem.wearableType || item.wearableType,
                material: formItem.material || item.material,
                style: formItem.style || item.style,
                fabric: formItem.fabric || item.fabric,
                itemName: formItem.itemName || item.itemName,
                tags: formItem.tags || item.tags,
              },
            };
          });

          const batchDamResult = await createBatchDamAssets(batchDamPayload);

          if (batchDamResult.statusCode && batchDamResult.statusCode !== 201 && batchDamResult.statusCode !== 200) {
            setIsFormSubmitting(false);
            const errorMsg = batchDamResult.message ? `${batchDamResult.message}` : `Failed to create assets in DAM. Please try again. (Error ${batchDamResult.statusCode})`;
            console.log('Batch DAM error:', errorMsg);
            messageApi.open({ type: 'error', content: errorMsg });
            return;
          }

          // Extract DAM IDs from batch response (array of created assets)
          const createdDamAssets: any[] = Array.isArray(batchDamResult.data) ? batchDamResult.data : [];
          if (createdDamAssets.length === 0) {
            setIsFormSubmitting(false);
            messageApi.open({ type: 'error', content: 'No assets were created in DAM batch call.' });
            return;
          }

          messageApi.open({ type: 'success', content: `${createdDamAssets.length} assets created in DAM successfully` });

          // Step 2: Call wearable add-item API with all items
          const wearableData = {
            wearableId: wearableCollectionId,
            itemList: createdDamAssets.map((damAsset: any, i: number) => {
              const item = bulkItems[i];
              const formItem = bulkFormItems[i] || {};
              // Use highest price from pricingDetails for wearable itemPrice
              const allFree = item.pricingModel.every(m => m === 'free' || m === 'freemium');
              const maxPrice = allFree ? 0 : Math.max(...Object.entries(item.pricingDetails)
                .filter(([k]) => k !== 'free' && k !== 'freemium')
                .map(([, v]) => v.price || 0));
              return {
                itemId: damAsset.id || damAsset._id || '',
                itemPrice: String(maxPrice),
                itemMetadata: {
                  name: formItem.itemName || item.itemName,
                  description: formItem.description || item.description || '',
                  image: item.image || '',
                  external_link: 'https://unifygptbrain.com',
                  attributes: [
                    { trait_type: 'type', value: 'Wearable' },
                    { trait_type: 'Category', value: formItem.wearableType || item.wearableType || '' },
                    { trait_type: 'Material', value: formItem.material || item.material || '' },
                    { trait_type: 'Style', value: formItem.style || item.style || '' },
                    { trait_type: 'Fabric', value: formItem.fabric || item.fabric || '' },
                  ],
                },
              };
            }),
          };

          const wearableResult = await createWearableItem(wearableData);

          if (wearableResult.statusCode && wearableResult.statusCode !== 201 && wearableResult.statusCode !== 200) {
            setIsFormSubmitting(false);
            const errorMsg = wearableResult.message ? `${wearableResult.message}` : `Failed to create wearable items. (Error ${wearableResult.statusCode})`;
            console.log('Wearable error:', errorMsg);
            messageApi.open({ type: 'error', content: errorMsg });
            return;
          }

          messageApi.open({ type: 'success', content: 'Wearable items created successfully!' });

          // Step 3: Create marketplace listings for each DAM asset
          const twoYearsLater = new Date();
          twoYearsLater.setFullYear(twoYearsLater.getFullYear() + 2);

          for (let i = 0; i < createdDamAssets.length; i++) {
            const damAsset = createdDamAssets[i];
            const item = bulkItems[i];
            const formItem = bulkFormItems[i] || {};
            const damId = damAsset.id || damAsset._id || '';
            const itemTags = formItem.tags || item.tags || '';

            const marketplaceData = {
              damId,
              assetType: assetTypeMongoId,
              purchaseOptions: item.pricingModel.map(model => ({
                purchaseType: pricingOptions.find(o => o.value === model)?.id || model,
                price: item.pricingDetails[model]?.price || 0,
                displayPrice: String(item.pricingDetails[model]?.price || 0),
                currency: usdCurrencyId,
                description: item.pricingDetails[model]?.description || '',
              })),
              visibility: 'public',
              isTimedListing: true,
              totalSupply: 999,
              availableCurrencies: ['USD'],
              fiatPaymentEnabled: true,
              listingDuration: twoYearsLater.toISOString(),
              retryEnabled: true,
              tags: itemTags ? itemTags.split(',').map((tag: string) => tag.trim()) : [],
            };

            try {
              const mpResult = await createMarketplaceListing(marketplaceData);
              if (mpResult.statusCode && mpResult.statusCode !== 201) {
                messageApi.open({ type: 'error', content: `Failed to list item ${i + 1} in marketplace.` });
              }
            } catch (error) {
              console.error(`Error creating marketplace listing for item ${i}:`, error);
            }
          }

          messageApi.open({ type: 'success', content: 'All wearable items listed successfully in Marketplace!' });

          setIsFormSubmitting(false);
          setTimeout(() => { navigate('/dashboard'); }, 1500);
          return;
        }

        // ---- SINGLE WEARABLE FLOW ----
        // Step 1: First call DAM API (same as normal flow)
        const damData = {
          name: formData.itemName,
          organizationId: authUser?.org_id || '',
          displayName: formData.itemName,
          iconUrl: formData.iconUrl || "",
          active: true,
          description: formData.description || "",
          digitalAssetTypeId: digitalAssetTypeId,
          assetOrigin: "internal",
          state: "DA_ready_to_execute",
          attributes: {
            itemType: formData.itemType,
            wearableType: formData.wearableType,
            material: formData.material,
            style: formData.style,
            fabric: formData.fabric,
            itemName: formData.itemName,          
            tags: formData.tags,
          }
        };

        const damResult = await createDamAsset(damData);

        if (damResult.statusCode && damResult.statusCode !== 201) {
          setIsFormSubmitting(false);
          const errorMsg = damResult.message ? `${damResult.message}` : `Failed to create asset in DAM. Please try again. (Error ${damResult.statusCode})`;
          console.log('DAM error:', errorMsg);
          messageApi.open({
            type: 'error',
            content: errorMsg,
          });
          return;
        }

        const newDamId = damResult.id;
        messageApi.open({
          type: 'success',
          content: 'Asset created successfully',
        });

        // Step 2: Call wearable API with DAM ID
        const wearableData = {
          wearableId: wearableCollectionId,
          itemList: [
            {
              itemId: newDamId, // Use DAM ID from DAM response
              itemPrice: (formData.pricingModel.includes('free') || formData.pricingModel.includes('freemium')) ? "0" : String(Object.values(formData.pricingDetails).reduce((sum, d) => sum + (d.price || 0), 0)),
              itemMetadata: {
                name: formData.itemName,
                description: formData.description || "",
                image: formData.iconUrl || "",
                external_link: "https://unifygptbrain.com",
                attributes: [
                  { trait_type: "type", value: "Wearable" },
                  { trait_type: "Category", value: formData.wearableType || "" },
                  { trait_type: "Material", value: formData.material || "" },
                  { trait_type: "Style", value: formData.style || "" },
                  { trait_type: "Fabric", value: formData.fabric || "" },
                ]
              }
            }
          ]
        };

        const wearableResult = await createWearableItem(wearableData);

        if (wearableResult.statusCode && wearableResult.statusCode !== 201 && wearableResult.statusCode !== 200) {
          setIsFormSubmitting(false);
          const errorMsg = wearableResult.message ? `${wearableResult.message}` : `Failed to create wearable item. Please try again. (Error ${wearableResult.statusCode})`;
          console.log('Wearable error:', errorMsg);
          messageApi.open({
            type: 'error',
            content: errorMsg,
          });
          return;
        }

        messageApi.open({
          type: 'success',
          content: 'Wearable item has been successfully created!',
        });

        // Create marketplace listing for wearable item
        const twoYearsLater = new Date();
        twoYearsLater.setFullYear(twoYearsLater.getFullYear() + 2);
        
        const wearableMarketplaceData = {
          damId: newDamId,
          assetType: assetTypeMongoId,
          // industry: [formData.industry],
          purchaseOptions: formData.pricingModel.map(model => ({
            purchaseType: pricingOptions.find(o => o.value === model)?.id || model,
            price: formData.pricingDetails[model]?.price || 0,
            displayPrice: String(formData.pricingDetails[model]?.price || 0),
            currency: usdCurrencyId,
            description: formData.pricingDetails[model]?.description || '',
          })),
          visibility: "public",
          isTimedListing: true,
          totalSupply: 999,
          availableCurrencies: ["USD"],
          fiatPaymentEnabled: true,
          listingDuration: twoYearsLater.toISOString(),
          retryEnabled: true,
          tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()) : [],
        };

        try {
          const wearableMarketplaceResult = await createMarketplaceListing(wearableMarketplaceData);
          
          if (wearableMarketplaceResult.statusCode && wearableMarketplaceResult.statusCode !== 201) {
            const errorMsg = wearableMarketplaceResult.message ? `${wearableMarketplaceResult.message}` : `Failed to create marketplace listing for wearable. Please try again.`;
            console.log('Marketplace error:', errorMsg);
            messageApi.open({
              type: 'error',
              content: errorMsg,
            });
            setIsFormSubmitting(false);
            return;
          }
          
          messageApi.open({
            type: 'success',
            content: 'Wearable item listed successfully in Marketplace!',
          });
        } catch (error) {
          console.error('Error creating wearable marketplace listing:', error);
          messageApi.open({
            type: 'error',
            content: 'Error creating marketplace listing for wearable',
          });
        }

        setIsFormSubmitting(false);
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
        return;
      }

      // Handle workflow item submission (DAM → Workflow add-item → Marketplace)
      if (isWorkflow) {
        const authUser = getStoredUser();
        const workflowCollectionId = process.env.REACT_APP_WORKFLOW_COLLECTION_ID || '';

        // Step 1: Create DAM asset
        const damData = {
          name: formData.itemName,
          organizationId: authUser?.org_id || '',
          displayName: formData.itemName,
          iconUrl: formData.iconUrl || '',
          active: true,
          description: formData.description || '',
          digitalAssetTypeId: digitalAssetTypeId,
          assetOrigin: 'internal',
          state: 'DA_ready_to_execute',
          attributes: {
            itemType: formData.itemType,
            itemName: formData.itemName,
            type: formData.type || '',
            industry: formData.industry || '',
            language: formData.language || '',
            tags: formData.tags || '',
            description: formData.description || '',
            version: formData.version || '',
            note: formData.note || '',
            content: formData.content || '',
            marketplaceId: formData.marketplaceId || '',
            lastVerifiedAt: formData.lastVerifiedAt || '',
            lastVerifiedBy: formData.lastVerifiedBy || '',
            encryptionStandard: formData.encryptionStandard || '',
            encriptionAtributes: formData.encriptionAtributes || {},
            licenses: [
              { terms: formData.terms_of_use || '' },
              { privacy_policy: formData.privacy_policy || '' },
            ],
          },
        };

        const damResult = await createDamAsset(damData);
        if (damResult.statusCode && damResult.statusCode !== 201) {
          setIsFormSubmitting(false);
          const errorMsg = damResult.message ? `${damResult.message}` : `Failed to create asset in DAM. Please try again. (Error ${damResult.statusCode})`;
          messageApi.open({ type: 'error', content: errorMsg });
          return;
        }

        const newDamId = damResult.id;
        messageApi.open({ type: 'success', content: 'Asset created successfully' });

        // Step 2: Call Workflow add-item API
        const workflowData = {
          marketplaceNFTId: workflowCollectionId,
          itemList: [
            {
              itemId: newDamId,
              itemPrice: (formData.pricingModel.includes('free') || formData.pricingModel.includes('freemium'))
                ? '0'
                : String(Object.values(formData.pricingDetails).reduce((sum, d) => sum + (d.price || 0), 0)),
              itemMetadata: {
                name: formData.itemName,
                description: formData.description || '',
                image: formData.iconUrl || '',
                external_link: 'https://unifygptbrain.com',
                attributes: [
                  { trait_type: 'type', value: formData.type || 'Workflow' },
                  { trait_type: 'Industry', value: formData.industry || '' },
                  { trait_type: 'Language', value: formData.language || '' },
                  { trait_type: 'Version', value: formData.version || '' },
                  { trait_type: 'Note', value: formData.note || '' },
                  { trait_type: 'Content', value: formData.content || '' },
                  { trait_type: 'MarketplaceId', value: formData.marketplaceId || '' },
                  { trait_type: 'LastVerifiedAt', value: formData.lastVerifiedAt || '' },
                  { trait_type: 'LastVerifiedBy', value: formData.lastVerifiedBy || '' },
                  { trait_type: 'EncryptionStandard', value: formData.encryptionStandard || '' },
                  { trait_type: 'EncriptionAtributes', value: formData.encriptionAtributes || '' },
                ],
              },
            },
          ],
        };

        const workflowResult = await createWorkflowItem(workflowData);
        if (workflowResult.statusCode && workflowResult.statusCode !== 201 && workflowResult.statusCode !== 200) {
          setIsFormSubmitting(false);
          const errorMsg = workflowResult.message ? `${workflowResult.message}` : `Failed to create workflow item. Please try again. (Error ${workflowResult.statusCode})`;
          messageApi.open({ type: 'error', content: errorMsg });
          return;
        }

        messageApi.open({ type: 'success', content: 'Workflow item has been successfully created!' });

        // Step 3: Create marketplace listing
        const twoYearsLater = new Date();
        twoYearsLater.setFullYear(twoYearsLater.getFullYear() + 2);
        const workflowMarketplaceData = {
          damId: newDamId,
          assetType: assetTypeMongoId,
          purchaseOptions: formData.pricingModel.map(model => ({
            purchaseType: pricingOptions.find(o => o.value === model)?.id || model,
            price: formData.pricingDetails[model]?.price || 0,
            displayPrice: String(formData.pricingDetails[model]?.price || 0),
            currency: usdCurrencyId,
            description: formData.pricingDetails[model]?.description || '',
          })),
          visibility: 'public',
          isTimedListing: true,
          totalSupply: 999,
          availableCurrencies: ['USD'],
          fiatPaymentEnabled: true,
          listingDuration: twoYearsLater.toISOString(),
          retryEnabled: true,
          tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()) : [],
        };

        try {
          const workflowMarketplaceResult = await createMarketplaceListing(workflowMarketplaceData);
          if (workflowMarketplaceResult.statusCode && workflowMarketplaceResult.statusCode !== 201) {
            const errorMsg = workflowMarketplaceResult.message ? `${workflowMarketplaceResult.message}` : 'Failed to create marketplace listing for workflow. Please try again.';
            messageApi.open({ type: 'error', content: errorMsg });
            setIsFormSubmitting(false);
            return;
          }
          messageApi.open({ type: 'success', content: 'Workflow item listed successfully in Marketplace!' });
        } catch (error) {
          console.error('Error creating workflow marketplace listing:', error);
          messageApi.open({ type: 'error', content: 'Error creating marketplace listing for workflow' });
        }

        setIsFormSubmitting(false);
        setTimeout(() => { navigate('/dashboard'); }, 1500);
        return;
      }

      // Handle skill item submission (DAM → Skill add-item → Marketplace)
      if (isSkill) {
        const authUser = getStoredUser();
        const skillCollectionId = process.env.REACT_APP_SKILL_COLLECTION_ID || '';

        // Step 1: Create DAM asset
        const damData = {
          name: formData.itemName,
          organizationId: authUser?.org_id || '',
          displayName: formData.itemName,
          ...(formData.iconUrl && { iconUrl: formData.iconUrl }),
          active: true,
          description: formData.description || '',
          digitalAssetTypeId: digitalAssetTypeId,
          assetOrigin: 'internal',
          state: 'DA_ready_to_execute',
          attributes: {
            itemType: formData.itemType,
            itemName: formData.itemName,
            type: formData.type || '',
            industry: formData.industry || '',
            language: formData.language || '',
            tags: formData.tags || '',
            description: formData.description || '',
            version: formData.version || '',
            note: formData.note || '',
            content: formData.content || '',
            marketplaceId: formData.marketplaceId || '',
            lastVerifiedAt: formData.lastVerifiedAt || '',
            lastVerifiedBy: formData.lastVerifiedBy || '',
            licenses: [
              { terms: formData.terms_of_use || '' },
              { privacy_policy: formData.privacy_policy || '' },
            ],
          },
        };

        const damResult = await createDamAsset(damData);
        if (damResult.statusCode && damResult.statusCode !== 201) {
          setIsFormSubmitting(false);
          const errorMsg = damResult.message ? `${damResult.message}` : `Failed to create asset in DAM. Please try again. (Error ${damResult.statusCode})`;
          messageApi.open({ type: 'error', content: errorMsg });
          return;
        }

        const newDamId = damResult.id;
        messageApi.open({ type: 'success', content: 'Asset created successfully' });

        // Step 2: Call Skill add-item API
        const skillData = {
          marketplaceNFTId: skillCollectionId,
          itemList: [
            {
              itemId: newDamId,
              itemPrice: (formData.pricingModel.includes('free') || formData.pricingModel.includes('freemium'))
                ? '0'
                : String(Object.values(formData.pricingDetails).reduce((sum, d) => sum + (d.price || 0), 0)),
              itemMetadata: {
                name: formData.itemName,
                description: formData.description || '',
                image: formData.iconUrl || '',
                external_link: 'https://unifygptbrain.com',
                attributes: [
                  { trait_type: 'type', value: formData.type || 'Skill' },
                  { trait_type: 'Industry', value: formData.industry || '' },
                  { trait_type: 'Language', value: formData.language || '' },
                  { trait_type: 'Version', value: formData.version || '' },
                  { trait_type: 'Note', value: formData.note || '' },
                  { trait_type: 'Content', value: formData.content || '' },
                  { trait_type: 'MarketplaceId', value: formData.marketplaceId || '' },
                  { trait_type: 'LastVerifiedAt', value: formData.lastVerifiedAt || '' },
                  { trait_type: 'LastVerifiedBy', value: formData.lastVerifiedBy || '' },
                ],
              },
            },
          ],
        };

        const skillResult = await createSkillItem(skillData);
        if (skillResult.statusCode && skillResult.statusCode !== 201 && skillResult.statusCode !== 200) {
          setIsFormSubmitting(false);
          const errorMsg = skillResult.message ? `${skillResult.message}` : `Failed to create skill item. Please try again. (Error ${skillResult.statusCode})`;
          messageApi.open({ type: 'error', content: errorMsg });
          return;
        }

        messageApi.open({ type: 'success', content: 'Skill item has been successfully created!' });

        // Step 3: Create marketplace listing
        const twoYearsLater = new Date();
        twoYearsLater.setFullYear(twoYearsLater.getFullYear() + 2);
        const skillMarketplaceData = {
          damId: newDamId,
          assetType: assetTypeMongoId,
          purchaseOptions: formData.pricingModel.map(model => ({
            purchaseType: pricingOptions.find(o => o.value === model)?.id || model,
            price: formData.pricingDetails[model]?.price || 0,
            displayPrice: String(formData.pricingDetails[model]?.price || 0),
            currency: usdCurrencyId,
            description: formData.pricingDetails[model]?.description || '',
          })),
          visibility: 'public',
          isTimedListing: true,
          totalSupply: 999,
          availableCurrencies: ['USD'],
          fiatPaymentEnabled: true,
          listingDuration: twoYearsLater.toISOString(),
          retryEnabled: true,
          tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()) : [],
        };

        try {
          const skillMarketplaceResult = await createMarketplaceListing(skillMarketplaceData);
          if (skillMarketplaceResult.statusCode && skillMarketplaceResult.statusCode !== 201) {
            const errorMsg = skillMarketplaceResult.message ? `${skillMarketplaceResult.message}` : 'Failed to create marketplace listing for skill. Please try again.';
            messageApi.open({ type: 'error', content: errorMsg });
            setIsFormSubmitting(false);
            return;
          }
          messageApi.open({ type: 'success', content: 'Skill item listed successfully in Marketplace!' });
        } catch (error) {
          console.error('Error creating skill marketplace listing:', error);
          messageApi.open({ type: 'error', content: 'Error creating marketplace listing for skill' });
        }

        setIsFormSubmitting(false);
        setTimeout(() => { navigate('/dashboard'); }, 1500);
        return;
      }

      // Normal flow for non-wearable items
      // First, prepare and submit to DAM API
      const authUser = getStoredUser();
      const damData = {
        name: formData.itemName,
        organizationId: authUser?.org_id || '',
        displayName: formData.itemName,
        iconUrl: formData.iconUrl || "",
        active: true,
        description: formData.description || "",
        // digitalAssetTypeId: "ea0dc317-cb33-49d5-94e6-dd46c7434c8a",
        digitalAssetTypeId: digitalAssetTypeId,
        assetOrigin: "internal",
        state: "DA_ready_to_execute",
        attributes: {
          itemType: formData.itemType,
          industry: formData.industry,
          language: formData.language,
          type: formData.type,
          itemName: formData.itemName,
          tags: formData.tags,
          description: formData.description,
          licenses: [
            {
              terms: formData.terms_of_use
            },
            {
              privacy_policy: formData.privacy_policy
            }
          ]
        }
      };

      // Step 1: Submit to DAM API via API helper
      const damResult = await createDamAsset(damData);
      // Check DAM API response status
      if (damResult.statusCode && damResult.statusCode !== 201 && damResult.statusCode !== 200) {
        setIsFormSubmitting(false);
        let errorMsg = '';
        
        // Extract error message from the response
        if (damResult.message) {
          errorMsg = damResult.message;
        } else if (damResult.error) {
          errorMsg = `${damResult.error}${damResult.statusCode ? ` (${damResult.statusCode})` : ''}`;
        } else {
          errorMsg = `Failed to create asset in DAM. Please try again.${damResult.statusCode ? ` (Error ${damResult.statusCode})` : ''}`;
        }
        
        console.log('DAM error:', errorMsg);
        messageApi.open({
          type: 'error',
          content: errorMsg,
        });
        return;
      }
      
      const newDamId = damResult.id;
      messageApi.open({
        type: 'success',
        content: 'Asset created successfully',
      });

      // Step 2: Submit to Marketplace API via API helper
      const twoYearsLater = new Date();
      twoYearsLater.setFullYear(twoYearsLater.getFullYear() + 2);
      const marketplaceData = {
        damId: newDamId,
        assetType: assetTypeMongoId,
        industry: [formData.industry],
        purchaseOptions: formData.pricingModel.map(model => ({
          purchaseType: pricingOptions.find(o => o.value === model)?.id || model,
          price: formData.pricingDetails[model]?.price || 0,
          displayPrice: String(formData.pricingDetails[model]?.price || 0),
          currency: usdCurrencyId,
          description: formData.pricingDetails[model]?.description || '',
        })),
        visibility: "public",
        isTimedListing: true,
        totalSupply: 999,
        availableCurrencies: ["USD"],
        fiatPaymentEnabled: true,
        listingDuration: twoYearsLater.toISOString(),
        retryEnabled: true,
        supportedInterfaces: formData.supportedInterfaces,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()) : [],
      };

      const marketplaceResult = await createMarketplaceListing(marketplaceData);
      
      // Check Marketplace API response status
      if (marketplaceResult.statusCode && marketplaceResult.statusCode !== 201) {
        setIsFormSubmitting(false);
        const errorMsg = marketplaceResult.message ? `${marketplaceResult.message}` : `Failed to create listing in Marketplace. Please try again.`;
        console.log('Marketplace error:', errorMsg);
        messageApi.open({
          type: 'error',
          content: errorMsg,
        });
        return;
      }
      
      messageApi.open({
        type: 'success',
        content: 'Listing created successfully in Marketplace',
      });

      // Final success message
      messageApi.open({
        type: 'success',
        content: 'Asset has been successfully published to the marketplace!',
      });
      
      setIsFormSubmitting(false);
      // Redirect to dashboard after a short delay to ensure messages are visible
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
      
    } catch (error: any) {
      setIsFormSubmitting(false);
      console.error('Submission error:', error?.response);
      
      // Handle axios error response
      if (error.response?.data?.message) {
        messageApi.open({
          type: 'error',
          content: error.response.data.message,
        });
      } else if (error instanceof Error) {
        messageApi.open({
          type: 'error',
          content: error.message,
        });
      } else {
        messageApi.open({
          type: 'error',
          content: 'Failed to create listing. Please try again.',
        });
      }
    }
  };

  const uploadProps = {
    name: 'file',
    multiple: false,
    showUploadList: false,
    accept: 'image/*',
    beforeUpload: (file: any) => {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        messageApi.open({
          type: 'error',
          content: 'Please upload only image files (JPG, PNG, etc.)',
        });
        return false;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        messageApi.open({
          type: 'error',
          content: 'File size must not exceed 5MB',
        });
        return false;
      }
      
      // Replace thumbnail (single file only)
      setFormData(prev => ({
        ...prev,
        thumbnail: [file]
      }));
      return false; // Don't auto upload to server
    },
  };

  const excelUploadProps = {
    name: 'file',
    multiple: false,
    showUploadList: false,
    accept: '.xlsx,.xls,.csv',
    beforeUpload: (file: any) => {
      const isValid =
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel' ||
        file.type === 'text/csv' ||
        file.name.endsWith('.xlsx') ||
        file.name.endsWith('.xls') ||
        file.name.endsWith('.csv');
      if (!isValid) {
        messageApi.open({
          type: 'error',
          content: 'Please upload only Excel (.xlsx, .xls) or CSV (.csv) files',
        });
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        messageApi.open({
          type: 'error',
          content: 'File size must not exceed 10MB',
        });
        return false;
      }
      setExcelFile(file);
      return false;
    },
  };

  const renderCurrentStep = () => {
    if (isWearable) {
      switch (currentStep) {
        case 0: return <WearableUpload styles={styles} formData={formData} setFormData={setFormData} isBulkUpload={isBulkUpload} setIsBulkUpload={setIsBulkUpload} excelFile={excelFile} setExcelFile={setExcelFile} uploadProps={uploadProps} excelUploadProps={excelUploadProps} />;
        case 1: return isBulkUpload ? <BulkWearableDetails styles={styles} bulkItems={bulkItems} /> : <WearableDetails styles={styles} formData={formData} />;
        case 2: return isBulkUpload ? <BulkPricingStep styles={styles} bulkItems={bulkItems} setBulkItems={setBulkItems} pricingOptions={pricingOptions} /> : <PricingStep styles={styles} formData={formData} setFormData={setFormData} pricingOptions={pricingOptions} isWearable={isWearable} />;
        case 3: return isBulkUpload ? <BulkReviewStep styles={styles} bulkItems={bulkItems} form={form} /> : <ReviewStep styles={styles} formData={formData} isWearable={isWearable} pricingOptions={pricingOptions} />;
        default: return <WearableUpload styles={styles} formData={formData} setFormData={setFormData} isBulkUpload={isBulkUpload} setIsBulkUpload={setIsBulkUpload} excelFile={excelFile} setExcelFile={setExcelFile} uploadProps={uploadProps} excelUploadProps={excelUploadProps} />;
      }
    } else if (isSkill) {
      switch (currentStep) {
        case 0: return <SkillUpload styles={styles} formData={formData} setFormData={setFormData} uploadProps={uploadProps} />;
        case 1: return <SkillDetails styles={styles} formData={formData} />;
        case 2: return <PricingStep styles={styles} formData={formData} setFormData={setFormData} pricingOptions={pricingOptions} isWearable={false} isSkill={isSkill} />;
        case 3: return <ReviewStep styles={styles} formData={formData} isWearable={false} isSkill={isSkill} pricingOptions={pricingOptions} />;
        default: return <SkillUpload styles={styles} formData={formData} setFormData={setFormData} uploadProps={uploadProps} />;
      }
    } else if (isWorkflow) {
      switch (currentStep) {
        case 0: return <WorkflowUpload styles={styles} formData={formData} setFormData={setFormData} uploadProps={uploadProps} />;
        case 1: return <WorkflowDetails styles={styles} formData={formData} />;
        case 2: return <PricingStep styles={styles} formData={formData} setFormData={setFormData} pricingOptions={pricingOptions} isWearable={false} isWorkflow={isWorkflow} />;
        case 3: return <ReviewStep styles={styles} formData={formData} isWearable={false} isWorkflow={isWorkflow} pricingOptions={pricingOptions} />;
        default: return <WorkflowUpload styles={styles} formData={formData} setFormData={setFormData} uploadProps={uploadProps} />;
      }
    } else {
      switch (currentStep) {
        case 0: return <BasicDetails styles={styles} />;
        case 1: return <UploadFiles styles={styles} formData={formData} setFormData={setFormData} uploadProps={uploadProps} />;
        case 2: return <LicenseTerms styles={styles} />;
        case 3: return <PricingStep styles={styles} formData={formData} setFormData={setFormData} pricingOptions={pricingOptions} isWearable={isWearable} />;
        case 4: return <ReviewStep styles={styles} formData={formData} isWearable={isWearable} pricingOptions={pricingOptions} />;
        default: return <BasicDetails styles={styles} />;
      }
    }
  };

  return (
    <div className={styles.addItemContainer}>
      {contextHolder}
      <CommonHeader />
      
      <main className={styles.addItemMain}>
        <div className={styles.formCard}>
          <div className={styles.breadcrumb}>
            {currentStep > 0 && (
              <>
                <span>Create Listing</span>
                <span>/</span>
                {currentStep === 1 && <span>Upload Files</span>}
                {currentStep === 2 && <span>Create Listing</span>}
                {currentStep === 3 && <span>List Your Agent</span>}
                {currentStep === 4 && <span>Agent Listing</span>}
                <span>/</span>
                {currentStep === 2 && <span>License Terms</span>}
                {currentStep === 3 && <span>Pricing and Interfaces</span>}
                {currentStep === 4 && <span>Review and Submit</span>}
              </>
            )}
          </div>

          <div className={styles.stepHeader}>
            <Steps current={currentStep} className={styles.stepsComponent}>
              {steps.map((step, index) => (
                <Step key={index} title={`Step ${index + 1} of ${steps.length}`} />
              ))}
            </Steps>
          </div>

          <div className={styles.formContent}>
            <Form 
              form={form} 
              layout="vertical" 
              className={styles.addItemForm}
              onValuesChange={(changedValues, allValues) => {
                // Exclude pricingModel â€” it's managed by handlePricingToggle
                const { pricingModel, ...rest } = changedValues;
                if (Object.keys(rest).length > 0) {
                  setFormData(prev => ({
                    ...prev,
                    ...rest
                  }));
                }
              }}
            >
              {renderCurrentStep()}
            </Form>
          </div>

          <div className={styles.formActions}>
            {currentStep > 0 && (
              <Button 
                onClick={handlePrev} 
                className={styles.backButton}
                icon={<LeftOutlined />}
              >
                {currentStep === 4 ? 'Edit' : 'Back'}
              </Button>
            )}
            
            {currentStep < steps.length - 1 ? (
              <Button 
                type="primary" 
                onClick={handleNext} 
                className={styles.nextButton}
                loading={isImageUploading}
                disabled={isImageUploading}
              >
                {isImageUploading ? 'Uploading Image...' : 'Next'}
              </Button>
            ) : (
              <Button 
                type="primary" 
                onClick={handleSubmit} 
                className={styles.submitButton}
                loading={isFormSubmitting}
                disabled={isFormSubmitting}
              >
                {isFormSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AddItemForm;